// AxonSwap — Multi-chain contract addresses

export const BSC_TESTNET_CHAIN_ID = 97
export const ARC_TESTNET_CHAIN_ID = 5042002

export interface ChainContracts {
  WBNB: string        // Wrapped native token (WBNB on BSC, WUSDC on ARC) — same WETH9 ABI
  Factory: string
  SwapRouter: string
  NonfungiblePositionManager: string
  Quoter: string
  QuoterV2: string
  Multicall2: string
  TickLens: string
  NFTDescriptor: string
  NFTPositionDescriptor: string
  AxonToken: string
  mUSDC: string
  mUSDT: string
  mWBTC: string
  mWETH: string
}

const BSC_CONTRACTS: ChainContracts = {
  WBNB: '0x83b6D2526FE323cf82Faf4Cd10A6781e222d1c23',
  Factory: '0x10B7422fE11A24373277be76d1E41571B076cb01',
  SwapRouter: '0x8114df7F9221917bF2C2e7b9C74a68aE1275df14',
  NonfungiblePositionManager: '0xA9a430f2954f3B399fFf15bdE7645e6d2C7231cE',
  Quoter: '0x409875E9AB2d79963C35b4AabDD10c75921CBAAF',
  QuoterV2: '0xF109aD22945DFe14A75CA25CaAfed8A34B6613E6',
  Multicall2: '0xC1E4fAf7E3F8e0d97251f566C6ffC53c08028868',
  TickLens: '0x35012b1fb420D8c26D62b5A2b296Bc05135A80dF',
  NFTDescriptor: '0xEFab4000dff44700CFCe9900535d24eE07316451',
  NFTPositionDescriptor: '0xCaeC072fFDDD168e55d500eC62683E84Fb21FFeA',
  AxonToken: '0x24483358c813aD89278f31CDA78f3Bc808d2bBDB',
  mUSDC: '0xBC14783680A73Ea119dcBCF59653dD4100a37efF',
  mUSDT: '0xFF24C8F61e412Ad86b5FfA66DC10F0d3f79670cc',
  mWBTC: '0x7ec1a956ccf76eFa8ab97C6E2D656B72F78A71bE',
  mWETH: '0xC717e1eE0D32239420CEB5DfE7da91Ae32C2E00B',
}

const ARC_CONTRACTS: ChainContracts = {
  WBNB: '0xa2654B3fBD6f8131fd7F74ee20DDCA2D20738A08',
  Factory: '0x8AAe40C3a2870c2A690d9Cbe5bC65961Cb33602e',
  SwapRouter: '0xE4ee59577F4b67932fe161c2b1028AEad3cdaE90',
  NonfungiblePositionManager: '0xa6a8578cf90aefc4204186092cbf6D73E44b1c37',
  Quoter: '0x83b6D2526FE323cf82Faf4Cd10A6781e222d1c23',
  QuoterV2: '0x10B7422fE11A24373277be76d1E41571B076cb01',
  Multicall2: '0xEDaa4770e6c44e5F0EfeB96cE67f271A039b19d3',
  TickLens: '0xd7bF1Eba3eAA0b7227952A8e889a2F936F98Fc5D',
  NFTDescriptor: '0xb3F5aA6aFe7F3bf5E5F7b18dD60F9841E90c6244',
  NFTPositionDescriptor: '0x7bBe8153D893A7D8C7D2e8BE37B1B8B437173e04',
  AxonToken: '0xC56F2DBB19638031232b4E00A925d6a8b1AE7B3e',
  mUSDC: '0x409875E9AB2d79963C35b4AabDD10c75921CBAAF',
  mUSDT: '0xF109aD22945DFe14A75CA25CaAfed8A34B6613E6',
  mWBTC: '0x35012b1fb420D8c26D62b5A2b296Bc05135A80dF',
  mWETH: '0xC1E4fAf7E3F8e0d97251f566C6ffC53c08028868',
}

// Backward compat — BSC as default
export const CONTRACTS = BSC_CONTRACTS

const CONTRACTS_BY_CHAIN: Record<number, ChainContracts> = {
  [BSC_TESTNET_CHAIN_ID]: BSC_CONTRACTS,
  [ARC_TESTNET_CHAIN_ID]: ARC_CONTRACTS,
}

export function getContracts(chainId: number): ChainContracts {
  return CONTRACTS_BY_CHAIN[chainId] || BSC_CONTRACTS
}

// Legacy PositionManagers (BSC only, pre-rebranding)
export const LEGACY_POSITION_MANAGERS: readonly string[] = [
  '0xC56F2DBB19638031232b4E00A925d6a8b1AE7B3e',
] as const

const LEGACY_PMS_BY_CHAIN: Record<number, readonly string[]> = {
  [BSC_TESTNET_CHAIN_ID]: ['0xC56F2DBB19638031232b4E00A925d6a8b1AE7B3e'],
  [ARC_TESTNET_CHAIN_ID]: [],
}

export function getLegacyPositionManagers(chainId: number): readonly string[] {
  return LEGACY_PMS_BY_CHAIN[chainId] || []
}

export const POOL_INIT_CODE_HASH = '0xe34f199b19b2b4f47f68442619d555527d244f78a3297ea89325f843f87b8b54'

// Fee tiers (in hundredths of a bip)
export const FEE_TIERS = {
  LOWEST: 100,   // 0.01%
  LOW: 500,      // 0.05%
  MEDIUM: 3000,  // 0.30%
  HIGH: 10000,   // 1.00%
} as const

export type FeeTier = typeof FEE_TIERS[keyof typeof FEE_TIERS]
